import type {
  PortalAssetCounts,
  PortalAssetFailureCode,
  PortalAssetLifecycle,
  PortalAssetMediaRef,
  PortalAssetTile,
} from '../../types/portalAsset.js';

// PORTAL ASSETS — what the grid SAYS about each tile (spec §8, commit 12). PURE,
// so every sentence is asserted without a DOM (the suite sits near the jsdom
// timeout cliff). Shared by the client page and the staff surfaces.
//
// ⚠️⚠️ AN IMAGE SLOT IS READ BESIDE THE LIFECYCLE, NEVER ALONE. `thumb: none`
// means "nothing to show" and says nothing about WHY: still uploading, still
// processing, failed, or (should never happen) ready with no rendition. A grid
// that drew one "no image" box for all four would make a failed upload look the
// same as one that is nearly done.
//
// ⚠️ `unavailable` is a SERVER-SIDE condition (a rendition exists and the server
// cannot sign a URL for it right now). It is not the club's problem and nothing
// they did, so the wording never suggests re-uploading.

export type TileImage =
  | { kind: 'image'; url: string }
  | { kind: 'uploading' }
  | { kind: 'processing' }
  | { kind: 'failed'; reason: string }
  /** A video longer than the Job will process: a policy, not a broken file. */
  | { kind: 'tooLong'; reason: string }
  | { kind: 'awaitingSupport' }
  | { kind: 'unavailable' }
  | { kind: 'missing' };

// ⚠️ The name a tile uses lives in displayName.ts, shared with the Name sort key so the two cannot drift.
export { assetDisplayName } from './displayName.js';

export const FAILED_FALLBACK_REASON = "We couldn't process this file.";

export function mediaImage(
  ref: PortalAssetMediaRef,
  lifecycle: PortalAssetLifecycle,
  failureReason: string | null,
  failureCode: PortalAssetFailureCode | null = null,
): TileImage {
  if (ref.state === 'signed') return { kind: 'image', url: ref.url };
  if (ref.state === 'unavailable') return { kind: 'unavailable' };
  switch (lifecycle) {
    case 'uploading':
      return { kind: 'uploading' };
    case 'processing':
      return { kind: 'processing' };
    case 'failed':
      return failureCode === 'video_too_long'
        ? { kind: 'tooLong', reason: failureReason?.trim() || VIDEO_TOO_LONG_FALLBACK_REASON }
        : { kind: 'failed', reason: failureReason?.trim() || FAILED_FALLBACK_REASON };
    case 'awaiting_support':
      return { kind: 'awaitingSupport' };
    case 'ready':
      return { kind: 'missing' };
  }
}

export function tileImage(t: Pick<PortalAssetTile, 'thumb' | 'lifecycle' | 'failureReason' | 'failureCode'>): TileImage {
  return mediaImage(t.thumb, t.lifecycle, t.failureReason, t.failureCode);
}

/** The words drawn where an image would be, for every non-image state. */
export function tileImagePlaceholder(img: TileImage): string | null {
  switch (img.kind) {
    case 'image':
      return null;
    case 'uploading':
      return 'Uploading…';
    case 'processing':
      return 'Processing…';
    case 'failed':
      return "Couldn't process";
    case 'tooLong':
      return VIDEO_TOO_LONG_PLACEHOLDER;
    case 'awaitingSupport':
      return AWAITING_SUPPORT_PLACEHOLDER;
    case 'unavailable':
      return 'Preview unavailable';
    case 'missing':
      return 'No preview';
  }
}

// ⚠️⚠️ `awaiting_support` NOW MEANS QUEUED (2026-10-08, the video Job). It was
// worded "video support is still being built" while no processor existed; the
// moment the Job ships that sentence is false, and a club told a feature does
// not exist will not come back to look. The stored value is unchanged, so the
// words moved instead. Still worded as OURS: the file is safe, nothing to do.
//
// ⚠️ THREE VIDEO STATES MUST READ DIFFERENTLY, and each is a test:
//   queued      awaiting_support            "Queued for processing"
//   too long    failed + video_too_long     "Too long to process" + the length rule
//   failed      failed, any other reason    "Couldn't process" + the reason
// A too-long clip is a POLICY, not a broken file: "Couldn't process" over it
// sends a club looking for a fault that is not there.
export const AWAITING_SUPPORT_PLACEHOLDER = 'Video queued';
export const AWAITING_SUPPORT_STATUS = 'Queued for processing — usually a couple of minutes';
export const AWAITING_SUPPORT_DETAIL =
  "This video is queued for processing. Once it's done you can watch it here and review it. Your file is stored safely — nothing needs re-uploading.";
export const VIDEO_TOO_LONG_PLACEHOLDER = 'Video too long';
export const VIDEO_TOO_LONG_STATUS = 'Too long to process';
export const VIDEO_TOO_LONG_FALLBACK_REASON = "This video is longer than we can process. Trim it and upload it again.";

export type StatusTone = 'waiting' | 'approved' | 'rejected' | 'working' | 'failed';

/**
 * One status per tile. ⚠️ DELETED outranks everything (a deleted approved asset
 * is not "Approved" — it is not usable), then lifecycle outranks review: an asset
 * still processing has a review status of `pending` by construction, and
 * "Waiting for review" on something nobody can see yet would be untrue.
 */
/**
 * The category line on a tile: the first name, plus a count when there are more. Nothing for none. The cross-account
 * staff view is named too, because the SERVER resolves names (a tile carries no brand id for a client to do it with).
 */
export function tileCategoryLabel(categories: readonly { name: string }[]): string {
  const [first, ...rest] = categories;
  if (!first) return '';
  return rest.length === 0 ? first.name : `${first.name} +${rest.length}`;
}

export function tileStatus(
  t: Pick<PortalAssetTile, 'lifecycle' | 'review'> & {
    deletedDaysLeft?: number | null;
    failureCode?: PortalAssetFailureCode | null;
  },
  /**
   * `short` (Run F): the GRID TILE says "Approved", not "Approved by Kyck". Scott's rule is "approved is approved,
   * whoever decided" (6afaf20e removed the staff badge); the detail view keeps the name and passes nothing.
   */
  opts: { short?: boolean } = {},
): { label: string; tone: StatusTone } {
  if (t.deletedDaysLeft !== undefined && t.deletedDaysLeft !== null) return { label: 'Deleted', tone: 'failed' };
  if (t.lifecycle === 'uploading') return { label: 'Uploading', tone: 'working' };
  if (t.lifecycle === 'processing') return { label: 'Processing', tone: 'working' };
  if (t.lifecycle === 'failed') {
    return t.failureCode === 'video_too_long'
      ? { label: VIDEO_TOO_LONG_STATUS, tone: 'failed' }
      : { label: "Couldn't process", tone: 'failed' };
  }
  if (t.lifecycle === 'awaiting_support') return { label: AWAITING_SUPPORT_STATUS, tone: 'working' };
  // ⚠️⚠️ THE TILE NAMES THE AUTHORITY, not just the status (2026-10-08). A club
  // and Kyck are equal reviewers, so "Approved" alone no longer says who decided.
  // `byName` is decided in tile.ts: "Kyck" for any staff member on a CLIENT tile
  // (Q4), the club person's name on either side.
  const by = t.review.byName && !opts.short ? ` by ${t.review.byName}` : '';
  switch (t.review.status) {
    case 'pending':
      return { label: 'Waiting for review', tone: 'waiting' };
    case 'approved':
      return { label: `Approved${by}`, tone: 'approved' };
    case 'rejected':
      return { label: `Not approved${by}`, tone: 'rejected' };
  }
}

/**
 * The banner a grid derives from its OWN tiles when previews cannot be shown —
 * deliberately not a response-level field (see PortalAssetMediaRef).
 */
export function unavailableBanner(tiles: readonly Pick<PortalAssetTile, 'thumb'>[]): string | null {
  const n = tiles.filter((t) => t.thumb.state === 'unavailable').length;
  if (n === 0) return null;
  return n === tiles.length
    ? "Previews can't be shown right now. Your files are safe; this is on our side and nothing needs re-uploading."
    : `${n} preview${n === 1 ? '' : 's'} can't be shown right now. Your files are safe; nothing needs re-uploading.`;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

export function formatDuration(ms: number | null): string | null {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return null;
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export type CountsSummary = {
  waiting: number;
  approved: number;
  rejected: number;
  working: number;
  failed: number;
  awaitingSupport: number;
  sentence: string;
};

/** The counts banner, summed over every account in view. Deleted is not shown (no view yet). */
type SummedKey = 'pending' | 'approved' | 'rejected' | 'processing' | 'failed' | 'awaitingSupport';

export function countsSummary(counts: readonly Pick<PortalAssetCounts, SummedKey>[]): CountsSummary {
  const sum = (k: SummedKey) => counts.reduce((n, c) => n + (Number.isFinite(c[k]) ? c[k] : 0), 0);
  const waiting = sum('pending');
  const approved = sum('approved');
  const rejected = sum('rejected');
  const working = sum('processing');
  const failed = sum('failed');
  const awaitingSupport = sum('awaitingSupport');
  const parts: string[] = [];
  if (waiting) parts.push(`${waiting} waiting for review`);
  if (approved) parts.push(`${approved} approved`);
  if (rejected) parts.push(`${rejected} not approved`);
  if (working) parts.push(`${working} still processing`);
  if (failed) parts.push(`${failed} couldn't be processed`);
  if (awaitingSupport) parts.push(`${awaitingSupport} video${awaitingSupport === 1 ? '' : 's'} queued for processing`);
  return {
    waiting,
    approved,
    rejected,
    working,
    failed,
    awaitingSupport,
    sentence: parts.length === 0 ? 'Nothing uploaded yet.' : parts.join(' · '),
  };
}

/**
 * ⚠️⚠️ THIS IS A SALES SURFACE, NOT AN ERROR STATE (Scott, 2026-10-09). The nav
 * item is live for EVERY portal user and the `assets` permission is held by a
 * handful, so for most people this sentence is all they will ever see of the
 * feature. It says what the library does for a club, then how to get it. Keep it
 * short, and keep the account-manager route.
 */
export const ASSETS_NOT_SWITCHED_ON =
  "Asset management is where your club's photos and videos live: upload them here, and once Kyck has approved them they can be used in your ads and social posts. It isn't switched on for your login yet. Ask your Kyck account manager and they can turn it on.";

/**
 * What a failed list call means to the person reading it.
 * ⚠️ 404 is the scope refusal (a location outside this login, or one that does
 * not exist — deliberately indistinguishable), and 403 is the missing `assets`
 * permission. Neither is "something went wrong, try again".
 */
export function listErrorMessage(status: number | null): { message: string; retry: boolean } {
  if (status === 404) return { message: "That location isn't one your login can see.", retry: false };
  if (status === 403) {
    return {
      message: ASSETS_NOT_SWITCHED_ON,
      retry: false,
    };
  }
  return { message: "We couldn't load your photos and videos. Try again in a moment.", retry: true };
}
